<template>
  <div class="fts-settings-page">
    <header class="fts-settings-head">
      <div>
        <h1>Password</h1>
        <p>One password for every workspace you belong to.</p>
      </div>
    </header>

    <section class="fts-settings-card">
      <div>
        <h2>Change password</h2>
        <p class="fts-settings-card-sub">
          Enter the one you use now, then the new one twice.
        </p>
      </div>

      <form autocomplete="off" class="fts-settings-password" @submit.prevent="save">
        <fts-form-element
          v-for="field in fields"
          :key="field.fieldName"
          :item="field"
          :form-data="formData"
          :validator="$v.formData"
          comfort-design
        />

        <div class="fts-settings-password-actions">
          <button
            type="submit"
            class="fts-settings-btn is-primary"
            :disabled="saving || !complete"
          >
            {{ saving ? "Changing…" : "Change password" }}
          </button>
        </div>
      </form>
    </section>
  </div>
</template>

<script>
/**
 * Settings › Your account › Password — the old profile modal's Password tab, moved into the frame.
 *
 * Same action, same three fields, same validators (`passwordValidator` for strength, `sameAs`
 * for the confirmation), so a password that was acceptable there is acceptable here and vice
 * versa. The fields are cleared after a change: a password left sitting in a form is a password
 * the next person at the screen can read.
 *
 * The server changes the SIGNED-IN user's password (`SecurityGuard.CurrentUser`; the `Email` on
 * its model is ignored) through Identity's ChangePasswordAsync. Whether that ends sessions on
 * other devices depends on how the refresh grant treats the security stamp, which has not been
 * checked -- so the page makes no claim about it either way, exactly as the old tab didn't.
 */
import { sameAs } from "vuelidate/lib/validators";
import commons from "../../commons/commons";
import { passwordValidator } from "../../commons/validator";
import { authorizationStore } from "../../store/authorizationStore";
import FTSFormElement from "../../elements/FTSFormElement";

function blank() {
  return { currentPassword: "", newPassword: "", confirmNewPassword: "" };
}

export default {
  name: "SettingsPassword",
  components: { "fts-form-element": FTSFormElement },
  props: {
    workspaceId: [String, Number],
  },
  data() {
    return {
      formData: blank(),
      saving: false,
    };
  },
  validations() {
    return { formData: commons.getFormValidators(this.fields) };
  },
  computed: {
    fields() {
      return [
        { input: "password", fieldName: "currentPassword", required: true },
        {
          input: "password",
          fieldName: "newPassword",
          required: true,
          validator: { password: passwordValidator },
        },
        {
          input: "password",
          fieldName: "confirmNewPassword",
          required: true,
          validator: { sameAs: sameAs(() => this.formData.newPassword) },
        },
      ];
    },
    complete() {
      const f = this.formData;
      return !!(f.currentPassword && f.newPassword && f.confirmNewPassword);
    },
  },
  methods: {
    async save() {
      if (this.saving) return;
      this.$v.formData.$touch();
      if (this.$v.formData.$invalid) {
        this.$toasted.global.invalidFormValidate();
        return;
      }
      this.saving = true;
      try {
        await authorizationStore.dispatch("changePassword", {
          currentPassword: this.formData.currentPassword,
          newPassword: this.formData.newPassword,
          confirmNewPassword: this.formData.confirmNewPassword,
        });
        this.formData = blank();
        this.$v.$reset();
        this.$toasted.success("Password updated successfully.");
      } catch (err) {
        this.$toasted.global.error(err, "Your password could not be changed.");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
